import { useState } from 'react';
import {
  BarChart3, Home, LogOut, Menu, X, Users, Settings2, MessagesSquare, GraduationCap, UserRound, School, Mic,
} from 'lucide-react';
import { NavLink, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import type { Bootstrap } from '../../shared/contracts';
import { useCanNTalk } from '../canntalk/context';
import { useIdentity } from '../canntalk/AccountContext';

const navigation = [
  { to: '/dashboard', zh: '練習首頁', en: 'Practice home', icon: Home },
  { to: '/placement', zh: '粵語口試', en: 'Spoken placement', icon: Mic },
  { to: '/scenarios', zh: '情境練習', en: 'Scenario practice', icon: Users },
  { to: '/progress', zh: '學習歷程', en: 'My progress', icon: BarChart3 },
];

export type AppOutletContext = { bootstrap: Bootstrap; onReset: () => Promise<void> };

export function useAppData() {
  return useOutletContext<AppOutletContext>();
}

export default function AppShell({ bootstrap, onLogout, onReset }: { bootstrap: Bootstrap; onLogout: () => void; onReset: () => Promise<void> }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { syncError, refresh } = useCanNTalk();
  const { identity } = useIdentity();
  const role = identity.user.role;
  const displayName = identity.user.name;
  const { pathname } = useLocation();
  const section = /^\/(setup|practice)\//.test(pathname) ? '/scenarios' : pathname.startsWith('/report/') ? '/progress' : pathname;
  return (
    <div className="app-frame">
      <header className="topbar">
        <button className="mobile-menu" onClick={() => setMenuOpen((value) => !value)} aria-label="Toggle navigation">
          {menuOpen ? <X /> : <Menu />}
        </button>
        <NavLink to={role === 'teacher' ? '/teaching' : '/dashboard'} className="brand" aria-label="CanNTalk dashboard">
          <span className="brand-mark" aria-hidden="true"><i /><i /><i /></span>
          <span className="brand-name">CanNTalk</span>
        </NavLink>
        <div className="brand-tagline"><b>用 AI，練出開口嘅信心</b><span>Cantonese in good company</span></div>
        <div className="topbar-actions">
          <span className="cnt-prototype-label">{role === 'admin' ? 'Administrator workspace' : role === 'teacher' ? 'Teaching workspace' : identity.kind === 'reviewer' ? 'CanNTalk · Review' : 'CanNTalk · Practice'}</span>
          <NavLink to="/account" className="profile cnt-account-link" aria-label="我的帳戶 My account"><span className="avatar">{displayName.slice(0, 1) || '用'}</span><span><b>{displayName}</b></span></NavLink>
          <button className="icon-button" onClick={onLogout} aria-label="Sign out"><LogOut size={19} /></button>
        </div>
      </header>

      <aside className={`sidebar ${menuOpen ? 'is-open' : ''}`}>
        <nav aria-label="Primary navigation">
          {(role === 'teacher' || role === 'admin') && <NavLink to="/teaching" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}><GraduationCap size={26} strokeWidth={1.8} /><span><b>教學總覽</b><small>My classes</small></span></NavLink>}
          {navigation.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive || section === item.to ? 'nav-item active' : 'nav-item'}>
              <item.icon size={26} strokeWidth={1.8} />
              <span><b>{item.zh}</b><small>{item.en}</small></span>
            </NavLink>
          ))}
          {role === 'admin' && <><NavLink to="/manage/users" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'nav-item active cnt-management-nav' : 'nav-item cnt-management-nav'}><UserRound size={26} strokeWidth={1.8} /><span><b>用戶管理</b><small>Accounts</small></span></NavLink><NavLink to="/manage/classes" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}><School size={26} strokeWidth={1.8} /><span><b>班級管理</b><small>Classes</small></span></NavLink><NavLink to="/manage/scenarios" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'nav-item active cnt-management-nav' : 'nav-item cnt-management-nav'}><Settings2 size={26} strokeWidth={1.8} /><span><b>情境工作室</b><small>Scenario studio</small></span></NavLink><NavLink to="/manage/ai" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'nav-item active cnt-management-nav' : 'nav-item cnt-management-nav'}><MessagesSquare size={26} strokeWidth={1.8} /><span><b>AI 對話設定</b><small>AI connection</small></span></NavLink></>}
        </nav>
        <div className="sidebar-foot">
          <div className="skyline-line" aria-hidden="true" />
          <p>由開口，到交流。<br />用廣東話，連繫香港。</p>
          <small>More conversations.<br />More Hong Kong.</small>
        </div>
      </aside>

      {menuOpen ? <button className="nav-scrim" onClick={() => setMenuOpen(false)} aria-label="Close navigation" /> : null}
      <main className="app-main">{syncError && <div className="cnt-sync-warning" role="status"><span>{syncError}</span><button className="cnt-text-button" onClick={() => void refresh()}>重試更新 Retry</button></div>}<Outlet context={{ bootstrap, onReset }} /></main>
    </div>
  );
}
